import React, { useLayoutEffect } from "react";
import { cn } from "./cn";
import { ABUNDO_MARK_URL } from './brandAssets';

/**
 * The auth card's title treatment, exported as `AuthCard.Title` so custom
 * states that compose their own children (the signup success panel) get the
 * same heading instead of hand-copying its classes. 20px sits between the
 * `bun-heading` and `bun-number` tiers by design: it is the auth hero size,
 * owned here rather than respecified per screen.
 */
function AuthCardTitle({ className, children }) {
  return (
    <h1
      className={cn(
        "m-0 mt-5 font-instrument text-[20px] font-semibold leading-tight tracking-[-0.01em] text-center text-bun-text",
        className
      )}
    >
      {children}
    </h1>
  );
}

/**
 * Bun auth shell: the centered card on the mist canvas that hosts login,
 * signup, forgot-password, and 2FA. Spec: Client Auth canvas + Component
 * Library "Auth & verification".
 *
 *   <AuthCard
 *     title="Welcome back"
 *     subtitle="Log in to your Abundo portal"
 *     below={<>New to Abundo? <a href="/signup">Create your account</a></>}
 *     footerLinks={[{ label: "Privacy", href: "/privacy-policy" }, ...]}
 *   >
 *     <form>...</form>
 *   </AuthCard>
 *
 * `logo` overrides the default Abundo "A" disc (e.g. a firm's own logo img).
 * Pass no title and compose children directly for custom states like the
 * signup success panel. `bare` skips the full-screen mist page wrapper
 * (gallery/specimen use).
 */
export default function AuthCard({
  logo,
  title,
  subtitle,
  below,
  footerLinks,
  bare,
  className,
  children,
}) {
  useLayoutEffect(() => {
    if (bare) return undefined;

    // Client Auth has a light canvas only. Keep it light even when an advisor
    // reaches it from a dark Bun screen, then restore the active app theme on
    // unmount. Remove this scope when a dark auth artboard is approved.
    const root = document.documentElement;
    const restoreDark = root.getAttribute("data-bun-theme") === "dark";
    root.removeAttribute("data-bun-theme");
    return () => {
      if (restoreDark) root.setAttribute("data-bun-theme", "dark");
    };
  }, [bare]);

  const column = (
    <div className={cn("w-[400px] max-w-full font-instrument", className)}>
      <div className="box-border bg-bun-surface border border-bun-line rounded-bun-xl px-5 pt-7 pb-6 shadow-bun-sm sm:px-9 sm:pt-9 sm:pb-8">
        {logo || (
          <img src={ABUNDO_MARK_URL} alt="Abundo" className="w-[38px] h-[38px] object-contain mx-auto" />
        )}
        {title && <AuthCardTitle>{title}</AuthCardTitle>}
        {subtitle && (
          <div className="text-bun-body font-book text-bun-stone text-center mt-[5px]">
            {subtitle}
          </div>
        )}
        {children}
      </div>
      {below && (
        <div className="text-center text-bun-body font-book text-bun-slate mt-[18px]">{below}</div>
      )}
    </div>
  );

  if (bare) return column;

  return (
    // .bun paints the canvas background itself, so the mist page tint lives
    // on a nested element — a bg-* utility on the same element as .bun loses
    // or wins by stylesheet order, which is a coin flip (see DESIGN.md).
    <div className="bun">
      <div className="min-h-screen box-border bg-bun-mist flex flex-col items-center justify-center px-4 py-6 sm:px-5 sm:py-10">
        {column}
        {footerLinks && footerLinks.length > 0 && (
          <div className="flex flex-wrap justify-center gap-x-[14px] mt-[14px] sm:mt-[26px] text-[11.5px] font-book font-instrument">
            {footerLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                {...(link.external && { target: "_blank", rel: "noopener noreferrer" })}
                className="inline-flex items-center min-h-11 sm:min-h-0 text-bun-stone hover:text-bun-blue no-underline"
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

AuthCard.Title = AuthCardTitle;
